<template>
  <section class="storage-content-tabs" @click="handlePaginationClick">
    <div class="storage-content-tabs__header">
      <div class="storage-content-tabs__nav" role="tablist" aria-label="Storage contents">
        <button
          v-for="tab in tabsState"
          :key="tab.key"
          type="button"
          role="tab"
          class="storage-content-tabs__tab"
          :class="{ 'is-active': activeTab === tab.key }"
          :aria-selected="activeTab === tab.key ? 'true' : 'false'"
          @click="activateTab(tab.key)"
        >
          {{ tab.label }} <span class="storage-content-tabs__count">{{ tab.count }}</span>
        </button>
      </div>
      <span v-if="activeState?.loading" class="storage-content-tabs__loading">Loading…</span>
    </div>

    <div v-if="activeState?.error" class="alert alert-danger">{{ activeState.error }}</div>
    <template v-else>
      <AppPagination
        v-if="activeState?.loaded"
        :pagination="pagination"
        aria-label="Storage content pagination"
        key-prefix="storage-content-page"
      />
      <div class="storage-content-tabs__table-wrap">
        <table class="table table-striped table-bordered table-condensed">
          <thead>
            <tr><th v-for="column in activeState?.columns || []" :key="column.key">{{ column.label }}</th></tr>
          </thead>
          <tbody>
            <tr v-if="activeState?.loading">
              <td :colspan="activeState.columns.length" class="text-center text-muted">Loading storage contents…</td>
            </tr>
            <tr v-else-if="activeState?.loaded && !paginatedRows.length">
              <td :colspan="activeState.columns.length" class="text-center text-muted">{{ activeState.emptyMessage }}</td>
            </tr>
            <tr v-for="row in paginatedRows" :key="`${activeTab}-${row.id}`">
              <td v-for="column in activeState?.columns || []" :key="column.key">{{ row[column.key] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import AppPagination from './AppPagination.vue'

const props = defineProps({
  tabs: { type: Array, default: () => [] }
})

const tabsState = ref((props.tabs || []).map((tab) => ({ ...tab, rows: [], loaded: false, loading: false, error: '' })))
const activeTab = ref(tabsState.value[0]?.key || '')
const pages = ref(Object.fromEntries(tabsState.value.map((tab) => [tab.key, 1])))
const perPage = 20
const activeState = computed(() => tabsState.value.find((tab) => tab.key === activeTab.value))

const pagination = computed(() => {
  const totalPages = Math.max(1, Math.ceil((activeState.value?.rows.length || 0) / perPage))
  const currentPage = Math.min(pages.value[activeTab.value] || 1, totalPages)
  return {
    current_page: currentPage,
    total_pages: totalPages,
    prev_page: currentPage > 1 ? currentPage - 1 : null,
    next_page: currentPage < totalPages ? currentPage + 1 : null
  }
})

const paginatedRows = computed(() => {
  const offset = (pagination.value.current_page - 1) * perPage
  return (activeState.value?.rows || []).slice(offset, offset + perPage)
})

async function activateTab(tabKey) {
  activeTab.value = tabKey
  await loadTab(tabKey)
}

async function loadTab(tabKey) {
  const tab = tabsState.value.find((entry) => entry.key === tabKey)
  if (!tab || tab.loaded || tab.loading) return

  tab.loading = true
  tab.error = ''
  try {
    const response = await fetch(tab.dataUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.message || `Request failed with status ${response.status}`)
    tab.rows = Array.isArray(payload.rows) ? payload.rows : []
    tab.loaded = true
  } catch (error) {
    tab.error = error.message || 'Failed to load storage contents.'
  } finally {
    tab.loading = false
  }
}

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) return
  event.preventDefault()
  const page = Number(link.dataset.page)
  if (Number.isFinite(page) && page > 0) pages.value[activeTab.value] = page
}

onMounted(() => loadTab(activeTab.value))
</script>

<style scoped>
.storage-content-tabs { margin-top: 24px; min-height: 180px; }
.storage-content-tabs__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.storage-content-tabs__nav { display: flex; flex-wrap: wrap; gap: 8px; padding: 5px; border: 1px solid #d9e2ec; border-radius: 14px; background: #f4f7fa; }
.storage-content-tabs__tab { padding: 9px 14px; border: 0; border-radius: 10px; background: transparent; color: #53677b; font-size: 13px; font-weight: 600; }
.storage-content-tabs__tab:hover { background: #e8eef5; color: #29465f; }
.storage-content-tabs__tab.is-active { background: #fff; color: #244a68; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1); }
.storage-content-tabs__count { display: inline-block; min-width: 22px; margin-left: 5px; padding: 1px 6px; border-radius: 999px; background: #dfe8f1; text-align: center; }
.storage-content-tabs__tab.is-active .storage-content-tabs__count { background: #dbeafe; color: #1d4f78; }
.storage-content-tabs__loading { color: #64748b; font-size: 13px; }
.storage-content-tabs__table-wrap { overflow-x: auto; }
.storage-content-tabs th { text-align: center; white-space: nowrap; }
.storage-content-tabs td { vertical-align: middle; }
</style>
